/**
 * StandKodKutusu.jsx — the stand-code box of the company portal (#487)
 *
 * Standlarım (64 px), the Dekoratör Projeler list (64 px) and the Anasayfa widgets (52 px chips, 40 px
 * outlined chip) each drew their own box and had drifted into three conventions: "—" over the code,
 * the code twice, "S11" over the code. One rule now:
 *   - the big line is the code (standNo only when a stand carries one — mock rows; live standNo is null
 *     by design, missing-pills-guide §8, "11AF43" is a code, not a number);
 *   - the small line exists only when standNo is the big line, and then holds the code — nothing repeats;
 *   - the text shrinks with its length and is cut with an ellipsis on one line; the tooltip carries the
 *     full stand name ("11.A10": the hall is in it) plus the caller's hint (status / "Detaylar").
 * A long catalogue code is data, not a bug (test seeds, a company name typed as the code), the box just
 * must not break.
 */
import React from "react";
import { Wrench } from "lucide-react";

/** { main, sub, full } — what the box says; see the rule above. */
export function standKodu(stand) {
  const code = stand?.standCode || stand?.standName || null;
  const hasNo = stand?.standNo != null && stand.standNo !== "";
  return {
    main: String(hasNo ? stand.standNo : code ?? "—"),
    sub: hasNo && code ? String(code) : null,
    full: stand?.standName || code || null,
  };
}

/** Font px that makes `len` bold characters fit the box: capped at a third of the size, never below 9. */
export function kodFontPx(len, size) {
  const cap = Math.round(size * 0.31);
  const fit = Math.floor((size - 8) / (Math.max(len, 1) * 0.62));
  return Math.max(9, Math.min(cap, fit));
}

/**
 * color: a hex / css colour for the filled box; `outline`: a bootstrap colour name for the outlined
 * variant (Anasayfa's Stand Durumu row). badgeTitle: shows the wrench "Dekorasyon tipi" badge.
 * hint: the caller's tooltip text (status, "Detaylar"), appended to the full stand name — unless the
 * hint already carries that name (Anasayfa's status texts do), then it stands alone.
 */
export default function StandKodKutusu({ stand, color, outline, size = 64, onClick, hint, badgeTitle }) {
  const { main, sub, full } = standKodu(stand);
  const title = (hint && full && hint.includes(full) ? hint : [full, hint].filter(Boolean).join(" — ")) || undefined;
  const interactive = onClick
    ? {
        role: "button",
        tabIndex: 0,
        onClick,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        },
      }
    : {};
  const look = outline
    ? { className: `border border-${outline} text-${outline}`, style: {} }
    : { className: "text-white", style: { background: color } };
  return (
    <div
      className={`position-relative rounded d-flex flex-column align-items-center justify-content-center flex-shrink-0 px-1 ${look.className}`}
      style={{ width: size, height: size, cursor: onClick ? "pointer" : undefined, ...look.style }}
      title={title}
      {...interactive}
    >
      {badgeTitle && (
        <span
          className="position-absolute top-0 start-0 translate-middle bg-success text-white rounded-circle d-flex align-items-center justify-content-center"
          style={{ width: 22, height: 22 }}
          title={badgeTitle}
        >
          <Wrench size={12} />
        </span>
      )}
      <div className="fw-bold lh-1 text-truncate mw-100" style={{ fontSize: kodFontPx(main.length, size) }}>{main}</div>
      {sub && <div className="lh-1 mt-1 text-truncate mw-100" style={{ fontSize: Math.max(9, Math.round(size * 0.16)) }}>{sub}</div>}
    </div>
  );
}
